import { zodResolver } from "@hookform/resolvers/zod";
import { FormControlLabel, Stack, Switch } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import MyLoadingButton from "../../../components/buttons/MyLoadingButton";
import PopoverInfo from "../../../components/display/PopoverInfo";
import ErrorFullScreenMessage from "../../../components/fullScreenMessage/ErrorFullScreenMessage";
import Skeletons from "../../../components/loader/Skeletons";
import { SnackbarContext } from "../../../contexts/Snackbar";
import * as AlertType from "../../../enums/AlertTypes";
import * as API_PATH from "../../../enums/ApiPaths";
import useUser from "../../../hooks/useUser";
import {
  isNullOrEmpty,
  isEmptyString,
  replaceEmptyStringsWithNull,
} from "../../../utils/Functions";
import usePutProfile from "../hooks/usePutProfile";
import EditInformationFields from "./EditInformationFields";
import * as ValidationValues from "./Validation/UserInformationValidation";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  EDITING_USER_DETAILS,
  LOGGED_IN_USER,
  USERS_CLINIC_SINGLE,
} from "../../../enums/QueryKeys";
import { useProfileSettingsState } from "../../../hooks/useProfileSettingsState";

export default function EditingInformationForm({
  setViewEditForm,
  profile,
  allowEmailEdit = false,
}) {
  const {
    isLoading,
    currentClinic,
    error,
    profile: userProfile,
    booleans,
    roles,
  } = useUser();

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { t } = useTranslation();
  const put = usePutProfile(API_PATH.EDIT_PROFILE + profile?.id);

  const methods = useForm({
    default: {
      username: userProfile.username,
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      phoneNumber: userProfile.phoneNumber,
      email: userProfile.email,
    },
    resolver: zodResolver(
      ValidationValues.useUserFormValidation(
        booleans.businessAccount,
        profile?.phoneNumber
      )
    ),
  });

//This hook handles the custom validation error and focuses for missing phone numbers.
  useProfileSettingsState(methods.control); 
  
  React.useEffect(() => {
    if (profile?.birthday) {
      methods.setValue("dob", dayjs(profile?.birthday));
    }
    if (profile?.location?.country) {
      methods.setValue("country", profile?.location?.country);
    }
  }, [methods, profile?.birthday, profile?.location?.country]);

  function resetToProfileView() {
    if (typeof setViewEditForm == "function") {
      setViewEditForm(false);
    }
    if (location.state?.missingPhoneNumber) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }

  const submitData = async (e) => {
    if (shouldUpdate(e)) {
      if (!allowEmailEdit) {
        e.email = profile?.email;
      }
      var data = {
        userDetails: {
          ...e,
          birthday:
            e?.dob != null && e?.dob != undefined
              ? dayjs(e?.dob).format("DD/MM/YYYY")
              : null,
          subscribeToEmails: roles.master ? false : e.subscribeToEmails,
          location: {
            address: e.address,
            place: e.place,
            province: e.province,
            zipcode: e.zipcode,
            city: e.city,
            country: e.country,
          },
        },
        clinic: roles.master ? null : currentClinic,
      };
      const filtered = replaceEmptyStringsWithNull(data);
      const res = await put.submitCallback(filtered);
      //After fixing userDto and loginDto remove this line of code
      if (res == true) {
        queryClient.invalidateQueries([LOGGED_IN_USER]);
        queryClient.invalidateQueries([USERS_CLINIC_SINGLE + profile?.id]);
        queryClient.invalidateQueries([EDITING_USER_DETAILS + profile?.id]);
        resetToProfileView();
      }
    } else {
      handleSnackbar("UpdateDataBeforeSending", AlertType.INFO);
    }
  };

  function shouldUpdate(data) {
    if (roles.master) {
      return true;
    }
    return (
      data?.firstName != profile?.firstName ||
      data?.lastName != profile?.lastName ||
      data?.username != profile?.userName ||
      data?.phoneNumber != profile?.phoneNumber ||
      (allowEmailEdit && data?.email != profile?.email) ||
      data?.clinic.id != profile?.clinic?.id ||
      data?.subscribeToEmails != profile?.isSubscribedToEmails
    );
  }

  if (isLoading) {
    return <Skeletons numOfSkeletons={12} grid={false} height={50} />;
  } else if (error) {
    return <ErrorFullScreenMessage />;
  }

  return (
    <form onSubmit={methods.handleSubmit(submitData)}>
      <Stack spacing={2}>
        <FormProvider {...methods}>
          <EditInformationFields
            profile={profile}
            allowEmailEdit={allowEmailEdit}
          />
        </FormProvider>

        {roles.employee && !isNullOrEmpty(profile?.email) ? (
          <PopoverInfo message={"WhatIsSubscribesToEmail"}>
            <FormControlLabel
              control={
                <Switch
                  defaultChecked={profile?.subscribeToEmails == "true"}
                  {...methods.register("subscribeToEmails")}
                />
              }
              label={t("SubscribeToEmails")}
            />
          </PopoverInfo>
        ) : null}
        <MyLoadingButton
          isLoading={put.loading}
          message={t("SaveChanges")}
          testId="profile.edit-form.save-button"
        />
      </Stack>
    </form>
  );
}

export function EditingFormUser({ setViewEditForm, profileProp = {} }) {
  const {
    isLoading,
    currentClinic,
    error,
    profile: userProfile,
    booleans,
    roles,
  } = useUser();

  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { t } = useTranslation();
  const put = usePutProfile(API_PATH.EDIT_PROFILE + profileProp?.id);

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const methods = useForm({
    default: {
      username: profileProp?.username,
      firstName: profileProp?.firstName,
      lastName: profileProp?.lastName,
      phoneNumber: profileProp?.phoneNumber,
      email: profileProp?.email,
      idCardNumber: profileProp?.idCardNumber,
      oib: profileProp?.oib,
    },
    resolver: zodResolver(
      ValidationValues.useUserFormValidation(
        booleans.businessAccount,
        userProfile.phoneNumber
      )
    ),
  });

  React.useEffect(() => {
    if (profileProp?.birthday) {
      methods.setValue("dob", dayjs(profileProp?.birthday));
    }
    if (profileProp?.location?.country) {
      methods.setValue("country", profileProp?.location?.country);
    }
    if (currentClinic) {
      methods.setValue("clinic", currentClinic);
    }
    if (isEmptyString(profileProp?.phoneNumber)) {
      methods.setError("phoneNumber", {
        type: "custom",
        message: t("PleaseAddAPhoneNumber"),
      });
      methods.setFocus("phoneNumber");
    }
  }, [currentClinic, methods, profileProp?.birthday, profileProp?.location?.country, profileProp?.phoneNumber, t]);

  function resetToProfileView() {
    if (typeof setViewEditForm == "function") {
      setViewEditForm(false);
    }
    //Clear the router state so it doesn't force the form open on re-renders
    if (location.state?.missingPhoneNumber) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }

  const submitData = async (e) => {
    if (shouldUpdate(e)) {
      e.email = profileProp?.email;
      var data = {
        userDetails: {
          ...e,
          birthday:
            e?.dob != null && e?.dob != undefined
              ? dayjs(e?.dob).format("DD/MM/YYYY")
              : null,
          subscribeToEmails: roles.master ? false : e.subscribeToEmails,
          location: {
            address: e.address,
            place: e.place,
            province: e.province,
            zipcode: e.zipcode,
            city: e.city,
            country: e.country,
          },
        },
        clinic: roles.master ? null : currentClinic,
      };
      const filtered = replaceEmptyStringsWithNull(data);
      const res = await put.submitCallback(filtered);
      //After fixing userDto and loginDto remove this line of code
      if (res == true) {
        queryClient.invalidateQueries([LOGGED_IN_USER]);
        resetToProfileView();
      }
    } else {
      handleSnackbar("UpdateDataBeforeSending", AlertType.INFO);
    }
  };

  function shouldUpdate(data) {
    if (roles.master) {
      return true;
    }
    return (
      data?.firstName != profileProp?.firstName ||
      data?.lastName != profileProp?.lastName ||
      data?.username != profileProp?.userName ||
      data?.phoneNumber != profileProp?.phoneNumber ||
      data?.clinic.id != profileProp?.clinic?.id ||
      data?.subscribeToEmails != profileProp?.isSubscribedToEmails
    );
  }

  if (isLoading) {
    return <Skeletons numOfSkeletons={12} grid={false} height={50} />;
  } else if (error) {
    return <ErrorFullScreenMessage />;
  }

  return (
    <form onSubmit={methods.handleSubmit(submitData)}>
      <Stack spacing={2}>
        <FormProvider {...methods}>
          <EditInformationFields profile={profileProp} />
        </FormProvider>

        {roles.employee && !isNullOrEmpty(profileProp?.email) ? (
          <PopoverInfo message={"WhatIsSubscribesToEmail"}>
            <FormControlLabel
              control={
                <Switch
                  defaultChecked={profileProp?.subscribeToEmails == "true"}
                  {...methods.register("subscribeToEmails")}
                />
              }
              label={t("SubscribeToEmails")}
            />
          </PopoverInfo>
        ) : null}
        <MyLoadingButton
          isLoading={put.loading}
          message={t("SaveChanges")}
          testId="profile.edit-form.save-button"
        />
      </Stack>
    </form>
  );
}
